import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BRAND from '../config/brand';
import { agreementApiUrl } from '../config/agreements';
import { BOOKING_PATH, MEETING_BOOKING_TYPE, clearPendingHold } from '../config/booking';
import { GOAL_SETTING_MEETING } from '../config/planningOffers';
import { trackMeetingBooked } from '../lib/analytics';
import { formatConfirmLabel } from '../lib/bookingSlots';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';
import './MeetingPages.css';

const MEETING = GOAL_SETTING_MEETING;
const SESSION_KEY = 'bpa_meeting_checkout_session';
const POLL_MS = 2500;
const MAX_POLLS = 12;

/** Read the Stripe session reference once, then keep it out of the address bar. */
function takeSessionId(search) {
  const fromUrl = new URLSearchParams(search).get('session_id') || '';
  try {
    if (fromUrl) {
      window.sessionStorage.setItem(SESSION_KEY, fromUrl);
      window.history.replaceState(window.history.state, '', window.location.pathname);
      return fromUrl;
    }
    return window.sessionStorage.getItem(SESSION_KEY) || '';
  } catch (_error) {
    return fromUrl;
  }
}

/**
 * After Stripe Checkout. The booking counts only when the server reports the
 * payment as received — arriving on this page is not treated as proof.
 */
export default function MeetingConfirmedPage() {
  const location = useLocation();
  const sessionId = useRef(takeSessionId(location.search)).current;
  const [result, setResult] = useState(sessionId ? { state: 'checking' } : { state: 'not_found' });

  useEffect(() => {
    if (!sessionId) return undefined;
    let stopped = false;
    let timer;
    let polls = 0;

    async function check() {
      polls += 1;
      let next;
      try {
        const response = await fetch(
          agreementApiUrl(`/api/checkout/hourly/status?session_id=${encodeURIComponent(sessionId)}`)
        );
        next = response.status === 404 ? { state: 'not_found' } : await response.json();
        if (!next?.state) next = { state: 'unknown' };
      } catch (_error) {
        next = { state: 'unknown' };
      }
      if (stopped) return;

      const settled =
        next.state === 'not_found' ||
        next.state === 'expired' ||
        (next.state === 'paid' && next.calendar !== 'pending');
      if (settled || polls >= MAX_POLLS) {
        setResult(next);
        return;
      }
      // Show what we know (e.g. paid, invite still on its way) and keep checking.
      setResult(next.state === 'paid' ? next : { state: 'checking' });
      timer = window.setTimeout(check, POLL_MS);
    }

    check();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [sessionId]);

  const paid = result.state === 'paid';

  useEffect(() => {
    if (!paid) return;
    clearPendingHold();
    trackMeetingBooked(sessionId, {
      offer_id: MEETING.offerId,
      booking_type: MEETING_BOOKING_TYPE,
      value: result.amountUsd ?? MEETING.price,
    });
  }, [paid, sessionId, result.amountUsd]);

  const when = result.startsAt ? formatConfirmLabel(result.startsAt) : '';

  return (
    <div className="public-linktree-page public-service-page">
      <PublicSiteNav />
      <main className="public-linktree-shell public-service-shell public-lead-shell">
        <section className="public-service-panel public-lead-card public-lead-success" aria-live="polite">
          <Link className="public-service-logo" to="/links" aria-label="Back to planning links">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" />
          </Link>
          <p className="public-linktree-kicker">{MEETING.name}</p>

          {result.state === 'checking' && (
            <>
              <h1>Confirming your payment…</h1>
              <p>This usually takes a few seconds. Please keep this page open.</p>
            </>
          )}

          {paid && (
            <>
              <h1>{result.calendar === 'needs_new_time' ? 'Payment received. We need a new time.' : 'You’re booked.'}</h1>
              {result.calendar === 'needs_new_time' ? (
                <div className="meeting-status meeting-status--attention">
                  <p>
                    Your {MEETING.priceLabel} payment went through, but the time you picked was no
                    longer open by the time it did. I’ve been notified and will email you to set a
                    new time. If none works, I’ll refund the {MEETING.priceLabel}.
                  </p>
                </div>
              ) : (
                <p>
                  Your {MEETING.priceLabel} payment was received
                  {when ? <> and your {MEETING.name} is set for <strong>{when}</strong></> : null}.
                </p>
              )}
              <ol className="meeting-detail-list meeting-detail-list--steps">
                <li>
                  <strong>Sign the meeting agreement.</strong>{' '}
                  {result.agreement === 'signed'
                    ? 'Done. Your signed copy was emailed to you.'
                    : 'I’ve emailed you a secure link. It covers this one meeting only. If you don’t see it in a few minutes, check spam.'}
                </li>
                {result.calendar !== 'needs_new_time' && (
                  <li>
                    <strong>Watch for the calendar invite.</strong>{' '}
                    {result.calendar === 'confirmed'
                      ? 'It’s on its way to the same email address.'
                      : 'It’s being sent now and should arrive shortly.'}
                  </li>
                )}
                <li>
                  <strong>Bring your questions.</strong> A short list of the decisions on your mind
                  is all the preparation you need.
                </li>
              </ol>
              <div className="meeting-terms">
                <p><strong>Changing your time.</strong> {MEETING.cancellationWording}</p>
                <p><strong>Refund.</strong> {MEETING.refundWording}</p>
              </div>
              <div className="public-service-actions public-service-actions--centered">
                <Link className="public-service-cta" to="/calculators">Explore the planning calculators</Link>
              </div>
            </>
          )}

          {(result.state === 'open' || result.state === 'processing' || result.state === 'unknown') && (
            <>
              <h1>We couldn’t confirm your payment yet.</h1>
              <p>
                If you completed payment, your confirmation email will arrive shortly and nothing
                more is needed. If you didn’t finish checking out, you have not been charged and
                you can pick a time again.
              </p>
              <div className="public-service-actions public-service-actions--centered">
                <Link className="public-service-cta public-service-cta--primary" to={BOOKING_PATH}>Back to booking</Link>
              </div>
            </>
          )}

          {(result.state === 'expired' || result.state === 'not_found') && (
            <>
              <h1>{result.state === 'expired' ? 'That checkout expired.' : 'No booking to show here.'}</h1>
              <p>
                {result.state === 'expired'
                  ? 'You have not been charged, and the time was released. You can pick a time again.'
                  : 'If you already booked, your confirmation is in your email. Otherwise you can pick a time below.'}
              </p>
              <div className="public-service-actions public-service-actions--centered">
                <Link className="public-service-cta public-service-cta--primary" to={BOOKING_PATH}>{MEETING.cta}</Link>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
